/**
 * EventConsentGate
 *
 * Componente de envoltura que monta un `ConsentCheckinModal` bloqueante
 * cuando el usuario actual tiene una inscripción al evento `eventId` cuyo
 * consentimiento (check-in de documentos) está pendiente.
 *
 * Uso (ej. en EventDetailPage):
 *   <EventConsentGate eventId={eventId}>
 *     <... contenido normal de la página ...>
 *   </EventConsentGate>
 *
 * Si no requiere check-in, renderiza children sin cambios.
 */

import { useState, useEffect } from "react";
import { useEventConsentRequired } from "@/hooks/useEventConsentRequired";
import ConsentCheckinModal from "./ConsentCheckinModal";
import { useAuthStore } from "@/stores/authStore";

interface EventConsentGateProps {
  eventId: string;
  /** Si true, no bloquea visualmente el contenido (modal igual aparece) */
  nonBlocking?: boolean;
  /** Si true, NO abre el modal automáticamente. Se controla externamente con `open`/`onOpenChange`. */
  manual?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactNode;
}

export function EventConsentGate({
  eventId,
  nonBlocking,
  manual,
  open: openProp,
  onOpenChange,
  children,
}: EventConsentGateProps) {
  const { profile } = useAuthStore();
  const { required, event, registration, member, person, refresh } =
    useEventConsentRequired(eventId);
  const [openInternal, setOpenInternal] = useState(false);
  const open = manual ? !!openProp : openInternal;
  const setOpen = (v: boolean) => {
    if (manual) onOpenChange?.(v);
    else setOpenInternal(v);
  };

  useEffect(() => {
    if (manual) return; // modo controlado, no auto-abrir
    if (required) setOpenInternal(true);
  }, [required, manual]);

  // Datos contextuales para el snapshot — usamos lo disponible
  const clubName = registration?.clubName ?? "";
  const associationName = profile?.scopeName ?? "Asociación";

  return (
    <>
      {(!required || nonBlocking) && children}
      {required && event && registration && member && person && (
        <ConsentCheckinModal
          isOpen={open}
          onClose={() => setOpen(false)}
          event={event}
          registration={registration}
          member={member}
          person={person}
          clubName={clubName}
          associationName={associationName}
          onCompleted={async () => {
            await refresh();
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

export default EventConsentGate;
